import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AppButton, AppBadge, AppField, AppInput } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconCheck, IconError } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";
import { readEntityPrefill, withEntityQuery } from "../../../shared/navigation/entityPrefill";
import type { ColumnDef } from "@tanstack/react-table";

type AssignmentFilter = "all" | "unassigned" | "assigned";

type DeviceRow = {
  id: number;
  serial_number: string;
  model: string;
  status: string;
  is_locked: boolean;
  is_paygo?: boolean;
  token_count?: number;
  starting_code?: number | null;
  value_divider?: number;
  customer_id?: number | null;
  customer_name?: string | null;
  customer_number?: string | null;
  customer_phone?: string | null;
  assignment?: "assigned" | "unassigned";
  created_at: string;
};

/**
 * Device inventory — stock (unassigned) vs linked to a customer, with customer
 * visible on the row so staff do not open detail to learn who owns a unit.
 */
export function DeviceDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPrefill = useMemo(() => readEntityPrefill(searchParams), [searchParams]);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<"loading" | "error" | "success">("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [assignment, setAssignment] = useState<AssignmentFilter>(() => {
    const a = urlPrefill.assignment;
    return a === "unassigned" || a === "assigned" ? a : "all";
  });
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchDevices = useCallback(async () => {
    setStatus("loading");
    try {
      const params = new URLSearchParams({ per_page: "100" });
      if (assignment !== "all") params.set("assignment", assignment);
      if (debouncedSearch) params.set("search", debouncedSearch);
      const data = await api.get<{ devices: DeviceRow[]; total: number }>(
        `/devices?${params}`,
      );
      setDevices(data.devices || []);
      setTotal(data.total ?? (data.devices || []).length);
      setStatus("success");
    } catch (err: unknown) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Failed to load devices");
    }
  }, [assignment, debouncedSearch]);

  useEffect(() => {
    void fetchDevices();
  }, [fetchDevices]);

  const columns: ColumnDef<DeviceRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "serial_number",
        header: "Serial Number",
      },
      {
        id: "assignment",
        header: "Assignment",
        cell: (c) => {
          const row = c.row.original;
          const linked = Boolean(row.customer_id);
          return linked ? (
            <AppBadge tone="success">
              <IconCheck size={12} /> Assigned
            </AppBadge>
          ) : (
            <AppBadge tone="info">Unassigned stock</AppBadge>
          );
        },
      },
      {
        id: "customer",
        header: "Customer",
        cell: (c) => {
          const row = c.row.original;
          if (!row.customer_id) {
            return <span className="pg-text-muted">—</span>;
          }
          return (
            <button
              type="button"
              className="pg-linklike"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/customers/${row.customer_id}`);
              }}
            >
              {row.customer_name || row.customer_number || `#${row.customer_id}`}
              {row.customer_number ? (
                <span className="pg-text-muted" style={{ marginLeft: 6, fontSize: "0.85em" }}>
                  {row.customer_number}
                </span>
              ) : null}
            </button>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (c) => {
          const val = c.getValue() as string;
          if (val === "active")
            return (
              <AppBadge tone="success">
                <IconCheck size={12} /> Active
              </AppBadge>
            );
          if (val === "unassigned") return <AppBadge tone="info">Unassigned</AppBadge>;
          if (val === "assigned") return <AppBadge tone="warning">Assigned</AppBadge>;
          return <AppBadge tone="warning">{val}</AppBadge>;
        },
      },
      {
        accessorKey: "model",
        header: "Model",
        cell: (c) => (c.getValue() as string | null) ?? "—",
        meta: { hideBelow: "md" },
      },
      {
        accessorKey: "token_count",
        header: "Counter",
        cell: (c) => {
          const row = c.row.original;
          if (!row.is_paygo) return "—";
          return <strong>{row.token_count ?? "—"}</strong>;
        },
        meta: { hideBelow: "md" },
      },
      {
        accessorKey: "is_locked",
        header: "Lock State",
        cell: (c) =>
          c.getValue() ? (
            <AppBadge tone="error">
              <IconError size={12} /> Locked
            </AppBadge>
          ) : (
            <AppBadge tone="success">
              <IconCheck size={12} /> Unlocked
            </AppBadge>
          ),
        meta: { hideBelow: "lg" },
      },
      {
        accessorKey: "created_at",
        header: "Registered",
        cell: (c) => {
          const iso = c.getValue() as string;
          if (!iso) return "—";
          return new Date(iso).toLocaleDateString();
        },
        meta: { hideBelow: "lg" },
      },
    ],
    [navigate],
  );

  const pageStatus = status === "success" && devices.length === 0 ? "empty" : status;

  const FILTERS: { value: AssignmentFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "unassigned", label: "Unassigned stock" },
    { value: "assigned", label: "Assigned to customer" },
  ];

  return (
    <>
      <PageHeader
        title="Devices"
        subtitle="Separate stock from units already linked to a household — customer shows on the row."
        actions={
          <>
            <Link to="/bulk-import">
              <AppButton type="button" variant="secondary">
                Bulk Import
              </AppButton>
            </Link>
            <Link to="/devices/new">
              <AppButton type="button">Register Device</AppButton>
            </Link>
          </>
        }
      />

      <div className="pg-list-toolbar">
        <AppField label="">
          <AppInput
            type="search"
            placeholder="Search serial…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 280 }}
            aria-label="Search device serial"
          />
        </AppField>
        <div className="pg-filter-chips" role="group" aria-label="Assignment filter">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={`pg-chip${assignment === f.value ? " active" : ""}`}
              onClick={() => setAssignment(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="pg-table-meta" style={{ marginLeft: "auto" }}>
          {total} device{total !== 1 ? "s" : ""}
        </span>
      </div>

      <PageState
        status={pageStatus}
        error={errorMsg}
        onRetry={fetchDevices}
        emptyTitle={
          assignment === "unassigned"
            ? "No unassigned stock"
            : assignment === "assigned"
              ? "No assigned devices"
              : "No devices"
        }
        emptyHint="Import from Excel or register a device."
      >
        <AppDataTable
          data={devices}
          columns={columns}
          onRowClick={(row) => {
            const path = `/devices/${row.serial_number}`;
            if (urlPrefill.customer_id && !row.customer_id) {
              navigate(withEntityQuery(path, { customer_id: urlPrefill.customer_id }));
            } else {
              navigate(path);
            }
          }}
        />
      </PageState>
    </>
  );
}
